import {computed, type ComputedRef} from 'vue';
import {useUserStore} from '@/ts/store/user-store';

enum Feature {
  EXPLORE_SECTION = 'EXPLORE_SECTION',
  SIDEBAR = 'SIDEBAR',
  HOME_SECTION = 'HOME_SECTION',
}

// Type representing the minimal user data needed for feature access checks
interface UserWithFeatureAccess {
  lastContract?: {
    license?: {
      blacklistedFeatures?: string[];
    };
  } | null;
}

function useFeatureAccess(): {
  canAccessExplore: ComputedRef<boolean>;
  canAccessBookmarks: ComputedRef<boolean>;
  canAccessSidebar: ComputedRef<boolean>;
  canAccessHome: ComputedRef<boolean>;
  hasAccessTo(feature: Feature, userData: UserWithFeatureAccess): boolean;
} {
  const userStore = useUserStore();

  // Generic helper method to check feature access from userData directly
  const hasAccessTo = (feature: Feature, userData: UserWithFeatureAccess | null | undefined): boolean => {
    return !userData?.lastContract?.license?.blacklistedFeatures?.includes(feature);
  };

  const canAccessExplore = computed(() => {
    return hasAccessTo(Feature.EXPLORE_SECTION, userStore.userData);
  });

  // The side panel stays open to a user without an active contract (it carries the crisis
  // resources), whether it lapsed or never existed, but their bookmarks are licensed content the
  // backend refuses to list.
  const canAccessBookmarks = computed(() => canAccessExplore.value && Boolean(userStore.userData?.activeContract));

  const canAccessSidebar = computed(() => {
    return hasAccessTo(Feature.SIDEBAR, userStore.userData);
  });

  const canAccessHome = computed(() => {
    return hasAccessTo(Feature.HOME_SECTION, userStore.userData);
  });

  return {
    canAccessExplore,
    canAccessBookmarks,
    canAccessSidebar,
    canAccessHome,
    hasAccessTo,
  };
}

export {Feature, useFeatureAccess};